import { useState, useEffect, useRef } from 'react'
import { Bug, Lightbulb } from 'lucide-react'
import { supabase } from '../lib/supabase'
import { APP_VERSION } from '../lib/appVersion'
import { logEvent } from '../lib/analytics'
import { Sheet } from './UI'

// ── Writers' Room (17) ────────────────────────────────────────────────────────
// The bug / idea door. One sheet: pick Bug or Idea, write the note, send. The
// route, build and viewport ride along silently so a report can be reproduced
// without asking. The old three-beat "episode" staging (2026-08) is gone; on 17 the
// note is the whole point and the sheet gets out of the way.
//
// Mounted once in App; opens on the `nextep:open-writers-room` window event
// ({ kind: 'bug'|'idea', prefill, source }). Writes go through submit_feedback()
// (SECURITY DEFINER, authenticated-only); guests have an anonymous session, so a
// user is always present.

const COUNT_KEY = 'nextep_writers_room_notes'
const EYE = { fontFamily: 'var(--font-mono)', fontSize: 10.5, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--amber)' }

const KINDS = {
  bug:  { Icon: Bug,       label: 'Something broke', placeholder: 'What happened, and what did you expect instead? Which screen, what you tapped.' },
  idea: { Icon: Lightbulb, label: 'An idea',         placeholder: 'What would you love NextEp to do? Dream a little, this is the room.' },
}

export function WritersRoom() {
  const [open,  setOpen]  = useState(false)
  const [phase, setPhase] = useState('write')   // write | sending | done
  const [kind,  setKind]  = useState('idea')
  const [msg,   setMsg]   = useState('')
  const [count, setCount] = useState(0)
  const taRef = useRef(null)

  useEffect(() => {
    const onOpen = (e) => {
      setKind(e.detail?.kind === 'bug' ? 'bug' : 'idea')
      setMsg(e.detail?.prefill || ''); setPhase('write'); setOpen(true)
      logEvent('writers_room_open', { source: e.detail?.source || 'unknown' })
    }
    window.addEventListener('nextep:open-writers-room', onOpen)
    return () => window.removeEventListener('nextep:open-writers-room', onOpen)
  }, [])
  useEffect(() => { if (open && phase === 'write') setTimeout(() => taRef.current?.focus(), 260) }, [open, phase])

  const close = () => setOpen(false)
  const submit = async () => {
    const message = msg.trim()
    if (message.length < 3 || phase !== 'write') return
    setPhase('sending')
    const context = {
      route: (typeof window !== 'undefined' ? window.location.pathname + window.location.hash : '') || '/',
      version: APP_VERSION,
      ua: (typeof navigator !== 'undefined' ? navigator.userAgent : '').slice(0, 240),
      viewport: (typeof window !== 'undefined' ? `${window.innerWidth}x${window.innerHeight}` : ''),
    }
    let ok = true
    try { const { error } = await supabase.rpc('submit_feedback', { p_type: kind, p_message: message, p_context: context }); if (error) ok = false } catch { ok = false }
    logEvent('writers_room_submit', { kind, ok })
    let n = 1
    try { n = (parseInt(localStorage.getItem(COUNT_KEY) || '0', 10) || 0) + 1; localStorage.setItem(COUNT_KEY, String(n)) } catch {}
    setCount(n); setPhase(ok ? 'done' : 'failed')
  }

  if (!open) return null
  const canSend = msg.trim().length >= 3 && phase === 'write'

  return (
    <Sheet open onClose={close} label="Writers’ Room" from="bottom" panelStyle={{ padding: '10px 20px calc(24px + env(safe-area-inset-bottom))', background: 'var(--bg-raised)' }}>
      <div style={{ width: 36, height: 4, borderRadius: 999, background: 'var(--border)', margin: '0 auto 16px' }} />
      {(phase === 'write' || phase === 'sending') && (
        <>
          <div style={EYE}>Writers’ Room</div>
          <div style={{ fontFamily: 'var(--font-serif)', fontSize: 24, fontWeight: 500, color: 'var(--text-strong)', lineHeight: 1.15, marginTop: 4 }}>Tell us what to fix, or what to build.</div>
          <div style={{ fontSize: 13.5, color: 'var(--text-muted)', marginTop: 6, lineHeight: 1.5 }}>Goes straight to the people who make NextEp. The screen you’re on and your build number come along, so you don’t need to describe them.</div>
          <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
            {Object.entries(KINDS).map(([key, k]) => { const on = kind === key; return (
              <button key={key} onClick={() => setKind(key)} aria-pressed={on} className="ui-hit" style={{ height: 36, padding: '0 14px', borderRadius: 'var(--radius-pill)', border: `1px solid ${on ? 'var(--amber)' : 'var(--border)'}`, background: on ? 'var(--amber)' : 'var(--bg-card)', color: on ? 'var(--amber-ink)' : 'var(--text)', fontFamily: 'var(--font)', fontSize: 14, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 7 }}>
                <k.Icon size={15} />{k.label}
              </button>
            ) })}
          </div>
          <textarea ref={taRef} value={msg} onChange={e => setMsg(e.target.value)} placeholder={KINDS[kind].placeholder} rows={5} disabled={phase === 'sending'}
            style={{ width: '100%', boxSizing: 'border-box', marginTop: 14, padding: '12px 14px', background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', color: 'var(--text)', fontSize: 15, lineHeight: 1.5, fontFamily: 'var(--font)', resize: 'none', outline: 'none' }} />
          <button onClick={submit} disabled={!canSend} className="ui-hit" style={{ marginTop: 14, width: '100%', height: 46, borderRadius: 'var(--radius-pill)', border: 'none', background: canSend ? 'var(--amber)' : 'var(--bg-card)', color: canSend ? 'var(--amber-ink)' : 'var(--text-dim)', fontFamily: 'var(--font)', fontSize: 15, fontWeight: 600, cursor: canSend ? 'pointer' : 'default' }}>
            {phase === 'sending' ? 'Sending…' : 'Send it'}
          </button>
        </>
      )}
      {phase === 'done' && (
        <div style={{ textAlign: 'center', padding: '8px 0 4px' }}>
          <div style={EYE}>Sent</div>
          <div style={{ fontFamily: 'var(--font-serif)', fontSize: 24, fontWeight: 500, color: 'var(--text-strong)', marginTop: 4 }}>{kind === 'bug' ? 'On it.' : 'Noted.'}</div>
          <div style={{ fontSize: 14, color: 'var(--text-muted)', marginTop: 8, lineHeight: 1.5 }}>
            {kind === 'bug' ? 'Thanks for catching it. Fixes ship most days.' : 'The best ideas in NextEp started exactly like this.'}
            {count > 1 && <> That’s {count} notes from you.</>}
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 18 }}>
            <button onClick={() => { setMsg(''); setPhase('write') }} className="ui-hit" style={{ flex: 1, height: 42, borderRadius: 'var(--radius-pill)', border: '1px solid var(--border)', background: 'var(--bg-card)', color: 'var(--text)', fontFamily: 'var(--font)', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>Another</button>
            <button onClick={close} className="ui-hit" style={{ flex: 1, height: 42, borderRadius: 'var(--radius-pill)', border: 'none', background: 'var(--amber)', color: 'var(--amber-ink)', fontFamily: 'var(--font)', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>Done</button>
          </div>
        </div>
      )}
      {phase === 'failed' && (
        <div style={{ textAlign: 'center', padding: '8px 0 4px' }}>
          <div style={EYE}>Didn’t send</div>
          <div style={{ fontFamily: 'var(--font-serif)', fontSize: 22, fontWeight: 500, color: 'var(--text-strong)', marginTop: 4 }}>Your note is still here.</div>
          <div style={{ fontSize: 14, color: 'var(--text-muted)', marginTop: 8 }}>Check your connection and try again.</div>
          <button onClick={() => setPhase('write')} className="ui-hit" style={{ marginTop: 16, width: '100%', height: 42, borderRadius: 'var(--radius-pill)', border: 'none', background: 'var(--amber)', color: 'var(--amber-ink)', fontFamily: 'var(--font)', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>Try again</button>
        </div>
      )}
    </Sheet>
  )
}
